import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import clone from 'ramda/src/clone';
import find from 'ramda/src/find';
import prop from 'ramda/src/prop';
import propEq from 'ramda/src/propEq';
import uniqBy from 'ramda/src/uniqBy';
import Avatar from '@components/@core/Avatar';
import BlogAdvancedSearch from '@components/BlogAdvancedSearch';
import Button from '@components/@core/AnimatedButton';
import EditDropdown from '@components/EditDropdown';
import Icon from '@components/@core/Icon';
import Pagination from '@components/@core/Pagination';
import Thumbnail from '@components/@core/Thumbnail';
import { getImageURL } from '~/services/util';

const Header = () => (
    <Row className="mx-0 border-bottom desktop-flex">
        <Col xs={1} className="px-0">
            <p className="dsl-m16 my-2">Image</p>
        </Col>
        <Col xs={4}>
            <p className="dsl-m16 my-2">Title</p>
        </Col>
        <Col xs={1} className="px-0">
            <p className="dsl-m16 my-2">Published</p>
        </Col>
        <Col xs={2} className="pr-0">
            <p className="dsl-m16 my-2">Categories</p>
        </Col>
        <Col xs={1} className="d-h-end">
            <Icon name="far fa-eye" size={14} />
        </Col>
        <Col xs={1} className="d-h-end">
            <Icon name="far fa-comment" size={14} />
        </Col>
        <Col xs={2} className="pl-5 pe-0">
            <p className="dsl-m16 my-2">Author</p>
        </Col>
    </Row>
);

const Blog = ({ data, editable, onEdit, onDetail }) => {
    const categoryNames = data?.data?.blog_categories ? data?.data?.blog_categories.join(', ') : 'Na';
    const url = getImageURL(data.data?.body);

    return (
        <Row className="d-flex ssm-column border-bottom mx-0 py-3" onClick={onDetail}>
            <Col xs={12} sm={1} className="px-0">
                <Thumbnail className="responsive-thumbnail" size="responsive" source={url} />
            </Col>
            <Col xs={12} sm={2} className="mobile-screen d-h-between my-2 my-sm-0 px-0">
                <div className="d-h-center flex-column">
                    <Avatar type="logo" url={data.author_avatar} isSponsored={true} />
                    <p className="dsl-b16 mb-0">{data.author?.first_name}</p>
                </div>
                {editable && <EditDropdown options={['Add new post', 'Delete', 'Edit']} onChange={onEdit} />}
            </Col>
            <Col xs={12} sm={4} className="d-flex px-0 px-sm-3 my-1 my-sm-0">
                <label className="dsl-b16 w-40 mobile-block">Title</label>
                <p className="dsl-b16 truncate-two text-400 mb-0 w-sm-content wrap-sm-title">{data?.name}</p>
            </Col>
            <Col xs={12} sm={1} className="px-0 d-flex my-1 my-sm-0">
                <label className="dsl-b16 w-40 mobile-block">Created At</label>
                <p className="dsl-b16 mb-0">{moment(data?.created_at).format('MMM DD, YY')}</p>
            </Col>
            <Col xs={12} sm={2} className="d-flex pe-0 ps-3 ssp-l-0 my-1 my-sm-0">
                <label className="dsl-b16 w-40 mobile-block">Category</label>
                <p className="dsl-b16 truncate-three mb-0">{categoryNames}</p>
            </Col>
            <Col xs={12} sm={1} className="d-h-start justify-content-sm-end px-0 px-sm-3 my-1 my-sm-0">
                <label className="dsl-b16 w-40 mobile-block">Views</label>
                <p className="dsl-b16 mb-2 ssm-b-0">{data?.stats?.views}</p>
            </Col>
            <Col xs={12} sm={1} className="d-h-start justify-content-sm-end px-0 px-sm-3 my-1 my-sm-0">
                <label className="dsl-b16 w-40 mobile-block">Comment</label>
                <p className="dsl-b16 mb-2 ssm-b-0">{data?.stats?.comments}</p>
            </Col>
            <Col xs={12} sm={2} className="d-h-between ps-5 ssp-l-5 pe-0 desktop-flex my-1 my-sm-0">
                <div className="d-h-center flex-column">
                    <Avatar type="logo" url={data.author_avatar} name={`${data.author?.first_name} ${data.author?.last_name}`} isSponsored={true} />
                    <p className="dsl-b16 mb-0 text-over">{data.author?.first_name}</p>
                </div>
                {editable && <EditDropdown options={['Add new post', 'Delete', 'Edit']} onChange={onEdit} />}
            </Col>
        </Row>
    );
};

const Published = ({ data, authors, categories, departments, search, onSelect, onChangePage, onSearch, onDetail }) => {
    const { current_page, last_page } = data;
    const posts = uniqBy(prop('id'), data?.data || []);

    const user = useSelector((state) => state.app.user?.community_user);

    const [searchTerms, setSearchTerms] = useState({
        search: null,
        author: null,
        department: null,
        category: null,
    });

    useEffect(() => {
        const emptyTerms = (!searchTerms.search || searchTerms.search === '') && !searchTerms.author && !searchTerms.category && !searchTerms.department;
        if (emptyTerms) {
            onSearch(null);
        } else {
            onSearch({
                search: searchTerms.search,
                author: searchTerms.author ? [searchTerms.author.id] : null,
                blogCategory: searchTerms.category ? [searchTerms.category] : null,
                categoryId: searchTerms.department ? [searchTerms.department.id] : null,
            });
        }
    }, [searchTerms]);

    const handleSelectMenu = (item) => (e) => {
        if (e === 'add new post') {
            onSelect('add new post');
        } else {
            onSelect(e, item);
        }
    };

    const handleSearch = (e) => {
        if (onSearch) onSearch(e);
        const author = e.author ? find(propEq('id', e.author[0]), authors) : null;
        const department = e.categoryId ? find(propEq('id', e.categoryId[0]), departments) : null;
        const category = e.blogCategory ? e.blogCategory[0] : null;
        setSearchTerms({ search: e.search, author, department, category });
    };

    const handleCancelSearchTerm = (type) => () => {
        const terms = clone(searchTerms);
        terms[type] = null;
        setSearchTerms(terms);
    };

    return (
        <div className="blog-admin-published">
            {search ? (
                <>
                    <p className="dsl-b20 bold mt-3 my-1">Search Results</p>
                    <div className="d-h-start py-2">
                        {searchTerms.author && (
                            <div className="search-chip" onClick={handleCancelSearchTerm('author')}>
                                <span className="dsl-b14">{`${searchTerms.author.first_name} ${searchTerms.author.last_name}`}</span>
                                <Icon name="fas fa-xmark cursor-pointer ms-2" color="#343f4b" size={14} />
                            </div>
                        )}
                        {searchTerms.search && searchTerms.search !== '' && (
                            <div className="search-chip" onClick={handleCancelSearchTerm('search')}>
                                <span className="dsl-b14">{searchTerms.search}</span>
                                <Icon name="fas fa-xmark cursor-pointer ms-2" color="#343f4b" size={14} />
                            </div>
                        )}
                        {searchTerms.category && (
                            <div className="search-chip" onClick={handleCancelSearchTerm('category')}>
                                <span className="dsl-b14">{searchTerms.category}</span>
                                <Icon name="fas fa-xmark cursor-pointer ms-2" color="#343f4b" size={14} />
                            </div>
                        )}
                        {searchTerms.department && (
                            <div className="search-chip" onClick={handleCancelSearchTerm('department')}>
                                <span className="dsl-b14">{searchTerms.department.name}</span>
                                <Icon name="fas fa-xmark cursor-pointer ms-2" color="#343f4b" size={14} />
                            </div>
                        )}
                    </div>
                </>
            ) : (
                <div className="d-h-end py-2">
                    <BlogAdvancedSearch authors={authors} categories={categories} departments={departments} onSearch={handleSearch} />
                    <Icon name="fas fa-chart-pie-alt" size={14} />
                </div>
            )}
            {posts.length > 0 ? (
                <>
                    <Header />
                    {posts.map((item) => {
                        const editable = !!find(propEq('id', user?.id), authors) || !!user?.super_admin;
                        return <Blog key={item.id} data={item} editable={editable} onEdit={handleSelectMenu(item)} onDetail={onDetail(item)} />;
                    })}
                </>
            ) : (
                <p className="dsl-b16">There are no published posts to show.</p>
            )}
            <div className={posts.length > 0 ? 'd-h-between' : 'd-h-end'}>
                <Pagination total={last_page} current={current_page} pers={[]} onChange={onChangePage} />
                <Button type="high" name="ADD POST" onClick={() => onSelect('add new post')} />
            </div>
        </div>
    );
};

Published.propTypes = {
    data: PropTypes.shape({
        current_page: PropTypes.number,
        data: PropTypes.array,
        last_page: PropTypes.number,
        per_page: PropTypes.number,
    }),
    authors: PropTypes.array,
    categories: PropTypes.array,
    departments: PropTypes.array,
    editable: PropTypes.bool,
    search: PropTypes.bool,
    onSelect: PropTypes.func,
    onChangePage: PropTypes.func,
    onSearch: PropTypes.func,
};

Published.defaultProps = {
    data: {
        current_page: 0,
        data: [],
        last_page: 0,
        per_page: 0,
    },
    authors: [],
    categories: [],
    departments: [],
    editable: false,
    search: false,
    onSelect: () => {},
    onChangePage: () => {},
    onSearch: () => {},
};

export default Published;
